import React, { useEffect, useMemo, useState } from "react";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getDbHeader } from "../../utils/util";
import { getOnsiteHeader } from "./utils";
import RegionWiseReport from "./RegionWiseReport";
import MuPb from "../../widgets/MuPb";
import { createEventDays, getCalendarDateKey } from "./eventDays";

// Display categories.
// Special Invitee is grouped under VIP.
// TRADE_DELEGATE and trade_delegate are grouped under DELEGATION.
const ALL_ROLES = [
  // "EXHIBITOR",
  "DELEGATION",
  "VIP",
  "EC",
  // "SERVICE",
  "MEDIA",
  "STUDENT",
  "VISITOR",
  "visitor",
];

const ROLE_LABELS = {
  EXHIBITOR: "Exhibitor",
  DELEGATION: "Delegation",
  VIP: "Special Invitee",
  EC: "EC",
  SERVICE: "Service",
  MEDIA: "Media",
  STUDENT: "Student",
  VISITOR: "Onsite Visitor",
  visitor: "Visitor",
};

const hourlyTemplate = {
  "9am-10am": 0,
  "10am-11am": 0,
  "11am-12pm": 0,
  "12pm-1pm": 0,
  "1pm-2pm": 0,
  "2pm-3pm": 0,
  "3pm-4pm": 0,
  "4pm-5pm": 0,
  "5pm-6pm": 0,
};

function getDateForDay(dayKey, eventDays) {
  const eventDay = eventDays.find(({ key }) => key === dayKey);
  return eventDay ? getCalendarDateKey(eventDay.date) : null;
}

function getHourlyBucket(dateObj) {
  const hour = dateObj.getHours();

  if (hour >= 9 && hour < 10) return "9am-10am";
  if (hour >= 10 && hour < 11) return "10am-11am";
  if (hour >= 11 && hour < 12) return "11am-12pm";
  if (hour >= 12 && hour < 13) return "12pm-1pm";
  if (hour >= 13 && hour < 14) return "1pm-2pm";
  if (hour >= 14 && hour < 15) return "2pm-3pm";
  if (hour >= 15 && hour < 16) return "3pm-4pm";
  if (hour >= 16 && hour < 17) return "4pm-5pm";
  if (hour >= 17 && hour < 18) return "5pm-6pm";

  return null;
}

const PIE_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#413ea0",
  "#a4de6c",
  "#d0ed57",
  "#00C49F",
  "#FFBB28",
];

function getColorForIndex(index) {
  const colors = [
    "#1c1c1a",
    "#b9838a",
    "#3357FF",
    "#FF33A6",
    "#FFC300",
    "#8E44AD",
    "#2ECC71",
  ];

  return colors[index % colors.length];
}

const calculateTodayKey = (eventDays) => {
  const todayKey = getCalendarDateKey(new Date());
  return (
    eventDays.find(({ date }) => getCalendarDateKey(date) === todayKey)?.key ||
    eventDays[0]?.key ||
    ""
  );
};

const DayWiseFootfallReport = () => {
  const { id } = useParams();
  const {
    data: Mdata,
    isLoading: mLoading,
    isError,
    error,
  } = useCommonGetInit(`${API_ENDPOINT}/user/check-onsite?id=${id}`);

  const [fdata, setFdata] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [eventDateRange, setEventDateRange] = useState(undefined);
  const [eventDatesError, setEventDatesError] = useState("");
  const [eventDatesLoading, setEventDatesLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState("");
  const [hiddenItems, setHiddenItems] = useState([]);
  const [visibleDays, setVisibleDays] = useState(new Set());

  const eventDays = useMemo(
    () =>
      eventDateRange
        ? createEventDays(
            eventDateRange.eventStartDate,
            eventDateRange.eventEndDate
          )
        : [],
    [eventDateRange]
  );
  const dayKeys = useMemo(
    () => eventDays.map(({ key }) => key),
    [eventDays]
  );
  const legendPayload = useMemo(
    () =>
      dayKeys.map((dayKey, index) => ({
        value: dayKey,
        type: "line",
        color: getColorForIndex(index),
      })),
    [dayKeys]
  );

  // =========================
  // 1. ROLE COUNTS (Cards)
  // =========================
  const roleCounts = useMemo(() => {
    const counts = {};

    if (Array.isArray(fdata)) {
      fdata.forEach((record) => {
        let role = record.role;

        // Include both trade delegate roles in Delegation.
        if (role === "TRADE_DELEGATE" || role === "trade_delegate") {
          role = "DELEGATION";
        }

        // Include Special Invitee alongside VIP.
        if (role === "Special Invitee") {
          role = "VIP";
        }

        if (!counts[role]) counts[role] = 0;
        counts[role]++;
      });
    }

    return counts;
  }, [fdata]);

  useEffect(() => {
    loadData();
  }, [Mdata]);

  useEffect(() => {
    loadEventDates();
  }, []);

  useEffect(() => {
    if (eventDays.length === 0) {
      setSelectedDay("");
      setVisibleDays(new Set());
      return;
    }

    setSelectedDay((currentDay) =>
      dayKeys.includes(currentDay)
        ? currentDay
        : calculateTodayKey(eventDays)
    );
    setVisibleDays(new Set(dayKeys));
  }, [dayKeys, eventDays]);

  const rolesWithCounts = ALL_ROLES.map((role) => ({
    role,
    count: roleCounts[role] || 0,
  }));

  // ====================================
  // 2. DAY-WISE ANALYSIS
  // ====================================

  // 2a. Day-wise Hourly Footfall
  const hourlyData = useMemo(() => {
    if (fdata.length === 0) return [];

    const hourlyCount = { ...hourlyTemplate };
    const currentDayDateString = getDateForDay(selectedDay, eventDays);

    if (!currentDayDateString) return [];

    for (const record of fdata) {
      if (record[selectedDay] === 1) {
        const timestampKey = `${selectedDay}timestamp`;
        const timestamp = record[timestampKey];

        if (timestamp) {
          const dateObj = new Date(timestamp);
          const dateOnly = getCalendarDateKey(dateObj);

          if (dateOnly === currentDayDateString) {
            const bucket = getHourlyBucket(dateObj);

            if (bucket && hourlyCount[bucket] !== undefined) {
              hourlyCount[bucket] += 1;
            }
          }
        }
      }
    }

    return Object.keys(hourlyCount).map((key) => ({
      name: key,
      count: hourlyCount[key],
    }));
  }, [eventDays, fdata, selectedDay]);

  async function loadData() {
    try {
      setIsLoading(true);

      const { data: MyData } = await axios.get(
        `${API_ENDPOINT}/onsite/scanned-users-footfall-small`,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.FOOTFALL_ANALYSIS)
      );

      const filteredData = MyData.filter(
        (item) => item.role !== "EXHIBITOR" && item.role !== "SERVICE"
      );

      setFdata(filteredData);
    } catch (error) {
      //toastError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadEventDates() {
    try {
      setEventDatesLoading(true);
      setEventDatesError("");

      const { data } = await axios.get(
        `${API_ENDPOINT}/event/get-event-dates`,
        getDbHeader()
      );
      const days = createEventDays(data?.eventStartDate, data?.eventEndDate);

      setEventDateRange(
        days.length > 0
          ? {
              eventStartDate: data.eventStartDate,
              eventEndDate: data.eventEndDate,
            }
          : null
      );
    } catch (error) {
      console.log(error);
      setEventDateRange(undefined);
      setEventDatesError("Unable to load event dates. Please try again.");
    } finally {
      setEventDatesLoading(false);
    }
  }

  // 2b. Day-wise Location-based Scan
  const dayWiseLocationData = useMemo(() => {
    if (!fdata || !Array.isArray(fdata)) return [];

    const currentDayDateString = getDateForDay(selectedDay, eventDays);
    if (!currentDayDateString) return [];

    const countsByLocation = {};

    fdata.forEach((record) => {
      if (record[selectedDay] === 1) {
        const timestampKey = `${selectedDay}timestamp`;
        const timestamp = record[timestampKey];

        if (timestamp) {
          const dateObj = new Date(timestamp);
          const dateOnly = getCalendarDateKey(dateObj);

          if (dateOnly === currentDayDateString) {
            const loc = record.scannedLocation || "Main Gate";
            countsByLocation[loc] = (countsByLocation[loc] || 0) + 1;
          }
        }
      }
    });

    return Object.keys(countsByLocation).map((loc) => ({
      location: loc,
      count: countsByLocation[loc],
    }));
  }, [eventDays, fdata, selectedDay]);

  // ========================================
  // 3. DAY-WISE COMPARISON (Line Chart)
  // ========================================
  const lineChartData = useMemo(() => {
    if (fdata.length === 0) return [];

    const hours = Object.keys(hourlyTemplate);
    const combinedData = hours.map((hour) => ({ name: hour }));

    for (const dayKey of dayKeys) {
      const dayDateString = getDateForDay(dayKey, eventDays);
      const dailyCounts = { ...hourlyTemplate };

      for (const record of fdata) {
        if (record[dayKey] === 1) {
          const timestampKey = `${dayKey}timestamp`;
          const timestamp = record[timestampKey];

          if (timestamp) {
            const dateObj = new Date(timestamp);
            const dateOnly = getCalendarDateKey(dateObj);

            if (dateOnly === dayDateString) {
              const bucket = getHourlyBucket(dateObj);

              if (bucket && dailyCounts[bucket] !== undefined) {
                dailyCounts[bucket] += 1;
              }
            }
          }
        }
      }

      for (let i = 0; i < combinedData.length; i++) {
        const hourKey = combinedData[i].name;
        combinedData[i][dayKey] = dailyCounts[hourKey];
      }
    }

    return combinedData;
  }, [dayKeys, eventDays, fdata]);

  const renderCustomLegendLine = () => {
    return (
      <Box display="flex" flexWrap="wrap" gap={2} ml={2}>
        {legendPayload.map((entry) => {
          const isActive = visibleDays.has(entry.value);

          return (
            <Box
              key={entry.value}
              onClick={() => {
                setVisibleDays((prev) => {
                  const newSet = new Set(prev);

                  if (newSet.has(entry.value)) {
                    newSet.delete(entry.value);
                  } else {
                    newSet.add(entry.value);
                  }

                  return newSet;
                });
              }}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: entry.color,
                  borderRadius: "50%",
                  marginRight: 1,
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  textDecoration: isActive ? "none" : "line-through",
                }}
              >
                {entry.value}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ===================================
  // 4. LOCATION-WISE (OVERALL) ANALYSIS
  // ===================================
  const overallLocationData = useMemo(() => {
    if (fdata.length === 0) return [];

    const countsByLocation = {};

    fdata.forEach((record) => {
      const loc = record.scannedLocation || "Main Gate";

      const attendanceDays = dayKeys.reduce(
        (sum, dayKey) => sum + (record[dayKey] || 0),
        0
      );

      countsByLocation[loc] = (countsByLocation[loc] || 0) + attendanceDays;
    });

    return Object.keys(countsByLocation).map((loc) => ({
      location: loc,
      count: countsByLocation[loc],
    }));
  }, [dayKeys, fdata]);

  // =============================
  // 5. DESIGNATION DISTRIBUTION
  // =============================
  const pieData = useMemo(() => {
    return rolesWithCounts.map(({ role, count }) => ({
      name: ROLE_LABELS[role] || role,
      value: count,
    }));
  }, [rolesWithCounts]);

  const handlePieLegendClick = (name) => {
    setHiddenItems((prev) =>
      prev.includes(name)
        ? prev.filter((item) => item !== name)
        : [...prev, name]
    );
  };

  const filteredPieData = pieData.filter(
    (entry) => !hiddenItems.includes(entry.name)
  );

  const renderCustomLegendPie = () => {
    return (
      <Box alignItems={"center"}>
        <ul
          style={{
            padding: "0",
            margin: "0",
            display: "flex",
            gap: "10px",
            listStyle: "none",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {pieData.map((entry, index) => (
            <li
              key={`item-${index}`}
              onClick={() => handlePieLegendClick(entry.name)}
              style={{
                cursor: "pointer",
                textDecoration: hiddenItems.includes(entry.name)
                  ? "line-through"
                  : "none",
                color: PIE_COLORS[index % PIE_COLORS.length],
                fontWeight: "bold",
                fontSize: "14px",
                transition: "color 0.3s, transform 0.3s",
              }}
              onMouseEnter={(e) => (e.target.style.transform = "scale(1.1)")}
              onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
            >
              {entry.name}
            </li>
          ))}
        </ul>
      </Box>
    );
  };

  if (mLoading || eventDatesLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{error?.message}</Alert>;
  }
  if (eventDatesError) {
    return <Alert severity="error">{eventDatesError}</Alert>;
  }
  if (!eventDateRange || eventDays.length === 0) {
    return (
      <Alert severity="warning">Event dates are not set for this event.</Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        Overall Footfall by Designation
      </Typography>

      {isLoading ? (
        <Typography>Loading data...</Typography>
      ) : (
        <>
          {/* 1. Role Counts Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {rolesWithCounts.map(({ role, count }) => (
              <Grid item xs={12} sm={6} md={3} lg={2} key={role}>
                <Card
                  variant="outlined"
                  sx={{ borderRadius: 2, textAlign: "center" }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {ROLE_LABELS[role] || role}
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                      {count}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* 2. Day-wise Analysis */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardHeader
              title={
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Daywise Analysis
                </Typography>
              }
              action={
                <Stack direction="row" spacing={2} alignItems="center" mr={2}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="day-select-label">Select Day</InputLabel>
                    <Select
                      labelId="day-select-label"
                      id="day-select"
                      value={selectedDay}
                      label="Select Day"
                      onChange={(e) => setSelectedDay(e.target.value)}
                    >
                      {eventDays.map(({ key, date }) => (
                        <MenuItem key={key} value={key}>
                          {key} — {date.toLocaleDateString()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <IconButton
                    onClick={() => {
                      loadData();
                      loadEventDates();
                    }}
                    color="primary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              }
            />

            <Divider />

            <CardContent>
              {/* 2a. Day-wise Hourly Footfall */}
              <Typography variant="h6" gutterBottom>
                Day-wise Hourly Footfall
              </Typography>

              <Box sx={{ width: "100%", height: 400, mb: 4 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={hourlyData}
                    margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />

                    <Bar dataKey="count" fill="#8884d8">
                      <LabelList dataKey="count" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* 2b. Day-wise Location-based Scan */}
              <Typography variant="h6" gutterBottom>
                Day-wise Location-based Scan
              </Typography>

              <Box sx={{ width: "100%", height: 400 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={dayWiseLocationData}
                    margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="location" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />

                    <Bar dataKey="count" fill="#82ca9d">
                      <LabelList dataKey="count" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* 3. Day-wise Comparison */}
          <Typography
            variant="h5"
            component="div"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            All Days Comparison
          </Typography>

          <Box sx={{ width: "100%", height: 400, mb: 4 }}>
            <ResponsiveContainer>
              <LineChart
                data={lineChartData}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend content={renderCustomLegendLine} />

                {dayKeys.map((dayKey, index) => {
                  const isActive = visibleDays.has(dayKey);

                  return (
                    <Line
                      key={dayKey}
                      type="monotone"
                      dataKey={dayKey}
                      stroke={getColorForIndex(index)}
                      dot={false}
                      activeDot={{ r: 8 }}
                      strokeOpacity={isActive ? 1 : 0}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* 4. Overall Location-based Scan */}
          <Typography
            variant="h5"
            component="div"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Overall Location-based Scan
          </Typography>

          <Box sx={{ width: "100%", height: 400, mb: 4 }}>
            <ResponsiveContainer>
              <BarChart
                data={overallLocationData}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="location" />
                <YAxis allowDecimals={false} />
                <Tooltip />

                <Bar dataKey="count" fill="#ffc658">
                  <LabelList dataKey="count" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Typography
            variant="h5"
            component="div"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Regionwise Report
          </Typography>

          {fdata && <RegionWiseReport data={fdata} />}

          {/* 5. Designation Distribution */}
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Designation Distribution
          </Typography>

          <Box sx={{ width: "100%", height: 400, mb: 4 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={filteredPieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={120}
                  fill="#8884d8"
                  label
                >
                  {filteredPieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>

                <Tooltip />
                <Legend content={renderCustomLegendPie} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DayWiseFootfallReport;
